import { iconSize, type IconSizeKey } from "../../tokens";

export type TextFieldVisualState = "default" | "focused" | "error" | "disabled";

export function getTextFieldVisualState({
  disabled,
  isFocused,
  state,
}: {
  disabled: boolean;
  isFocused: boolean;
  state: "default" | "error";
}): TextFieldVisualState {
  if (disabled) return "disabled";
  if (state === "error") return "error";
  if (isFocused) return "focused";
  return "default";
}

/**
 * Icon size per field size. Small binds Figma's `icon-md`, so it takes the key
 * and steps up from 20 to 24 on desktop web. Medium binds no variable (24 in
 * both modes), so it stays the token's 24 at every width.
 */
export const textFieldIconSizeMap = {
  sm: "md",
  md: iconSize.lg,
} as const satisfies Record<"sm" | "md", IconSizeKey | number>;

/**
 * The input's tap area (molecules audit 2026-10-30, Form #18; DS-020 and
 * DS-031: 44 effective). A small field is 40 tall, as Figma draws it, so below
 * 768 its input is 44 tall and centred: it reaches 2px past the field's top
 * and bottom whatever the border, and the text stays where it was. Native
 * always; web on touch screens only. From 768 the small field is 48, and the
 * medium one is 48 or more, so the input fills the field as before.
 * (Tailwind finds classes only when they're written out whole.)
 */
export const textFieldInputReachClass = {
  // Below 768 only (`max-md:`), so wider screens keep the stretched input
  sm: [
    "native:max-md:h-[44px] native:max-md:self-center",
    "[@media(pointer:coarse)]:max-md:h-[44px] [@media(pointer:coarse)]:max-md:self-center",
  ].join(" "),
  md: "",
} as const satisfies Record<"sm" | "md", string>;

/**
 * Native: slop that makes the trailing action's tap area 44×44 around its
 * icon. It stops at the input (the 8px gap), so a tap in the text still places
 * the caret, and makes up the width on the outer side. On a phone, at `sm` it
 * reaches 2px past the field's top and bottom and 3px past its right edge.
 */
export const textFieldTrailingHitSlop = {
  sm: { top: 12, bottom: 12, left: 8, right: 16 },
  md: { top: 10, bottom: 10, left: 8, right: 12 },
} as const;

/**
 * Web: the same area as `textFieldTrailingHitSlop`, drawn by an invisible
 * `::before` when the browser reports a coarse primary pointer (a touch
 * screen). With a fine pointer the button keeps its icon's box. On desktop
 * the small icon is 24, so the area there is 48×48, the field's height.
 */
export const textFieldTrailingTouchClass = {
  sm: "[@media(pointer:coarse)]:relative [@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:content-[''] [@media(pointer:coarse)]:before:inset-y-[-12px] [@media(pointer:coarse)]:before:left-[-8px] [@media(pointer:coarse)]:before:right-[-16px]",
  md: "[@media(pointer:coarse)]:relative [@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:content-[''] [@media(pointer:coarse)]:before:inset-y-[-10px] [@media(pointer:coarse)]:before:left-[-8px] [@media(pointer:coarse)]:before:right-[-12px]",
} as const;

/** Icon color class per visual state */
export const textFieldIconClass: Record<TextFieldVisualState, string> = {
  default: "text-luce-neutral-foreground-intermediate",
  focused: "text-luce-neutral-foreground-intermediate",
  error: "text-luce-neutral-foreground-intermediate",
  disabled: "text-luce-neutral-foreground-attenuated",
};

/** Input text color class per visual state */
export const textFieldInputClass: Record<TextFieldVisualState, string> = {
  default: "text-luce-neutral-foreground-default",
  focused: "text-luce-neutral-foreground-default",
  error: "text-luce-neutral-foreground-default",
  disabled: "text-luce-neutral-foreground-attenuated",
};

type PlaceholderColor = "neutral-foreground-intermediate" | "neutral-foreground-attenuated";

/**
 * Placeholder colour per visual state, as a Typography colour: the one source
 * for fields that draw their placeholder as text (Select, DatePicker) and for
 * `textFieldPlaceholderClass`. Figma fades it while the field is focused
 * (153:2090) or disabled; TextArea's set differs, so it has its own.
 */
export const textFieldPlaceholderColor: Record<TextFieldVisualState, PlaceholderColor> = {
  default: "neutral-foreground-intermediate",
  focused: "neutral-foreground-attenuated",
  error: "neutral-foreground-intermediate",
  disabled: "neutral-foreground-attenuated",
};

// Whole class names, so Tailwind finds them
const placeholderClassByColor: Record<PlaceholderColor, string> = {
  "neutral-foreground-intermediate": "placeholder:text-luce-neutral-foreground-intermediate",
  "neutral-foreground-attenuated": "placeholder:text-luce-neutral-foreground-attenuated",
};

/** Placeholder color class per visual state, for an input's own placeholder */
export const textFieldPlaceholderClass: Record<TextFieldVisualState, string> = {
  default: placeholderClassByColor[textFieldPlaceholderColor.default],
  focused: placeholderClassByColor[textFieldPlaceholderColor.focused],
  error: placeholderClassByColor[textFieldPlaceholderColor.error],
  disabled: placeholderClassByColor[textFieldPlaceholderColor.disabled],
};

/**
 * Search's clear × colour: attenuated at rest, intermediate while the field is
 * focused or in error, as Figma binds it (8159:9450, 8159:9498)
 */
export const textFieldClearIconClass: Record<TextFieldVisualState, string> = {
  default: "text-luce-neutral-foreground-attenuated",
  focused: "text-luce-neutral-foreground-intermediate",
  error: "text-luce-neutral-foreground-intermediate",
  disabled: "text-luce-neutral-foreground-attenuated",
};
